import { useState, useEffect } from 'react'
import { Plus, Edit2, ToggleLeft, ToggleRight, Trash2, UserCog } from 'lucide-react'
import toast from 'react-hot-toast'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import assessoresService from '../../services/assessoresService'

// Cadastro de "Assessor" — vinculado a um LOTE ESPECÍFICO (fluxo cavalo), com
// uma % configurada na hora de vincular, calculada só sobre o valor daquele
// lote. NÃO confundir com o módulo "Promotor" (frontend/src/pages/promotores/
// PromotoresPage.jsx), que é vinculado ao leilão inteiro e já é rotulado
// "Assessoria" na interface pro fluxo cavalo — são duas entidades diferentes
// e independentes.
export default function AssessoresPage() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [assessores, setAssessores] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState({ nome: '', telefone: '', cpf_cnpj: '', percentual_padrao: '' })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (orgId) carregar()
  }, [orgId])

  const carregar = async () => {
    setLoading(true)
    try {
      const data = await assessoresService.listar(orgId)
      setAssessores(data)
    } catch { }
    finally { setLoading(false) }
  }

  const abrirNovo = () => {
    setEditando(null)
    setForm({ nome: '', telefone: '', cpf_cnpj: '', percentual_padrao: '' })
    setShowModal(true)
  }

  const abrirEditar = (a) => {
    setEditando(a)
    setForm({
      nome: a.nome,
      telefone: a.telefone || '',
      cpf_cnpj: a.cpf_cnpj || '',
      percentual_padrao: a.percentual_padrao != null ? String(a.percentual_padrao) : '',
    })
    setShowModal(true)
  }

  const handleSalvar = async () => {
    if (!form.nome.trim()) { toast.error('Informe o nome'); return }
    setSaving(true)
    try {
      const payload = {
        nome: form.nome.trim(),
        telefone: form.telefone || null,
        cpf_cnpj: form.cpf_cnpj || null,
        percentual_padrao: form.percentual_padrao !== '' ? parseFloat(form.percentual_padrao) : null,
      }
      if (editando) {
        await assessoresService.atualizar(orgId, editando.id, payload)
        toast.success('Assessor atualizado')
      } else {
        await assessoresService.criar(orgId, payload)
        toast.success('Assessor criado')
      }
      setShowModal(false)
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  const handleToggle = async (a) => {
    try {
      await assessoresService.toggle(orgId, a.id)
      toast.success(a.ativo ? 'Assessor inativado' : 'Assessor ativado')
      carregar()
    } catch { toast.error('Erro ao alterar status') }
  }

  const handleExcluir = async (a) => {
    if (!confirm(`Excluir assessor "${a.nome}"?`)) return
    try {
      await assessoresService.excluir(orgId, a.id)
      toast.success('Assessor excluído')
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir')
    }
  }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCog className="w-5 h-5 text-indigo-600" />
          <h1 className="text-lg font-semibold text-gray-800">Assessores</h1>
        </div>
        <button onClick={abrirNovo}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" /> Novo Assessor
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-8 text-gray-400">Carregando...</div>
        ) : assessores.length === 0 ? (
          <div className="text-center py-8 text-gray-400">Nenhum assessor cadastrado.</div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {assessores.map(a => (
                <div key={a.id} className={`p-4 ${!a.ativo ? 'opacity-50' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-800 truncate">{a.nome}</span>
                      <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5">
                        {a.telefone && <span>{a.telefone}</span>}
                        {a.cpf_cnpj && <span>{a.cpf_cnpj}</span>}
                        {a.percentual_padrao != null && (
                          <span className="font-medium text-indigo-700">%: {a.percentual_padrao}%</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${a.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {a.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                      <button onClick={() => abrirEditar(a)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleToggle(a)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg">
                        {a.ativo ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleExcluir(a)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Telefone</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">CPF/CNPJ</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">% Padrão</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {assessores.map(a => (
                    <tr key={a.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!a.ativo ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 font-medium text-gray-800">{a.nome}</td>
                      <td className="px-4 py-3 text-gray-600">{a.telefone || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{a.cpf_cnpj || '—'}</td>
                      <td className="px-4 py-3 text-center font-semibold text-indigo-700">
                        {a.percentual_padrao != null ? `${a.percentual_padrao}%` : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${a.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {a.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => abrirEditar(a)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Editar">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleToggle(a)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg" title={a.ativo ? 'Inativar' : 'Ativar'}>
                            {a.ativo ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleExcluir(a)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal Criar/Editar */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">{editando ? 'Editar Assessor' : 'Novo Assessor'}</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 font-medium mb-1">Nome *</label>
                <input type="text" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Nome do assessor" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Telefone</label>
                  <input type="text" value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="(00) 00000-0000" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">CPF/CNPJ</label>
                  <input type="text" value={form.cpf_cnpj} onChange={e => setForm({ ...form, cpf_cnpj: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="000.000.000-00" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 font-medium mb-1">% Padrão</label>
                <input type="number" step="0.01" min="0" max="100" value={form.percentual_padrao}
                  onChange={e => setForm({ ...form, percentual_padrao: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="0.00" />
                <p className="mt-1 text-xs text-gray-400">Só pré-preenche a % ao vincular o assessor num lote — pode ser alterada em cada lote.</p>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium">
                Cancelar
              </button>
              <button onClick={handleSalvar} disabled={saving}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {saving ? 'Salvando...' : editando ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
